export type PackageFormTab =
  | 'details'
  | 'batches'
  | 'itinerary'
  | 'inclusions'
  | 'logistics'
  | 'content'
  | 'seo';

export type PackageFormFieldError = {
  path: string;
  label: string;
  message: string;
  tab: PackageFormTab;
};

const PACKAGE_FIELD_LABELS: Record<string, string> = {
  slug: 'Slug',
  name: 'Name',
  shortDescription: 'Short description',
  tripTypeId: 'Trip category',
  destinationId: 'Destination',
  preferredMonths: 'Preferred months',
  numberOfNights: 'Nights',
  numberOfDays: 'Days',
  price: 'Total price (₹)',
  strikePrice: 'Strike price (₹)',
  hotelCost: 'Hotel cost (₹)',
  flightCost: 'Flight cost (₹)',
  status: 'Status',
  rating: 'Rating (0-5)',
  packageCategory: 'Package category',
  packageTheme: 'Package theme',
  isInternational: 'International',
  overviewHtml: 'Overview',
  visaInsuranceHtml: 'Visa & insurance',
  costSummaryHtml: 'Other info',
  paymentTerms: 'Payment terms',
  metaTitle: 'Meta title',
  metaDescription: 'Meta description',
  metaKeywords: 'Meta keywords',
  ogImageId: 'Social share image',
};

const PACKAGE_FIELD_TAB: Record<string, PackageFormTab> = {
  overviewHtml: 'content',
  visaInsuranceHtml: 'content',
  paymentTerms: 'content',
  costSummaryHtml: 'inclusions',
  metaTitle: 'seo',
  metaDescription: 'seo',
  metaKeywords: 'seo',
  ogImageId: 'seo',
};

const PAYMENT_TERM_SEGMENT_LABELS: Record<string, string> = {
  label: 'Payment term label',
  value: 'Payment term value',
};

export const humanisePackageFieldName = (path: string): string => {
  if (PACKAGE_FIELD_LABELS[path]) return PACKAGE_FIELD_LABELS[path];

  const paymentTermMatch = /^paymentTerms\.(\d+)\.(\w+)$/.exec(path);
  if (paymentTermMatch) {
    const index = paymentTermMatch[1];
    const segment = paymentTermMatch[2];
    if (index && segment) {
      const segmentLabel = PAYMENT_TERM_SEGMENT_LABELS[segment] ?? segment;
      return `${segmentLabel} (row ${Number(index) + 1})`;
    }
  }

  const cleaned = path
    .split('.')
    .filter((seg) => !/^\d+$/.test(seg))
    .map((seg) => seg.replace(/([A-Z])/g, ' $1').toLowerCase())
    .join(' ');
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export const packageFieldTab = (path: string): PackageFormTab => {
  const root = path.split('.')[0] ?? path;
  return PACKAGE_FIELD_TAB[root] ?? 'details';
};

export const collectPackageFormErrors = (
  errors: Record<string, unknown>,
  prefix = '',
): PackageFormFieldError[] => {
  const out: PackageFormFieldError[] = [];

  for (const key of Object.keys(errors)) {
    const value = errors[key] as Record<string, unknown> | undefined;
    if (!value) continue;

    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
      out.push({
        path,
        label: humanisePackageFieldName(path),
        message: value.message,
        tab: packageFieldTab(path),
      });
      continue;
    }

    if (typeof value === 'object') {
      out.push(...collectPackageFormErrors(value as Record<string, unknown>, path));
    }
  }

  return out;
};

export const formatPackageValidationToast = (errors: PackageFormFieldError[]): string => {
  if (errors.length === 0) return 'Please fix the highlighted fields and try again.';
  if (errors.length === 1) {
    const e = errors[0]!;
    return `Please fix "${e.label}": ${e.message}`;
  }
  return `Please fix ${errors.length} fields: ${errors.map((e) => `"${e.label}" (${e.message})`).join('; ')}`;
};

const cssEscape = (value: string): string => value.replace(/(["\\])/g, '\\$1');

/** Scroll to and focus the first invalid field when possible. */
export const focusFirstPackageFormError = (path: string | null): void => {
  if (!path) return;
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(`[name="${cssEscape(path)}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof (el as HTMLInputElement).focus === 'function') {
      (el as HTMLInputElement).focus({ preventScroll: true });
    }
  }, 0);
};

export const handlePackageFormValidationErrors = (
  errors: Record<string, unknown>,
  setActiveTab: (tab: PackageFormTab) => void,
): PackageFormFieldError[] => {
  const collected = collectPackageFormErrors(errors);
  const first = collected[0];
  if (first) setActiveTab(first.tab);
  focusFirstPackageFormError(first?.path ?? null);
  return collected;
};
